import React, { useCallback, useEffect, useState } from "react";
import { Plus, Eye, Pencil, Send, Undo2, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KIND_LABELS, fmtDate, romeMonth } from "@/lib/marketing";
import ConfirmModal from "@/components/ConfirmModal";
import { ContentEditor } from "@/components/admin/marketing/ContentEditor";
import { PublishDialog } from "@/components/admin/marketing/PublishDialog";
import { EmailLogDialog } from "@/components/admin/marketing/EmailLogDialog";
import { PreviewDialog } from "@/components/admin/marketing/PreviewDialog";
import { PlanSummary } from "@/components/admin/marketing/PlanSummary";

const STATUS = {
  draft: ["Bozza", "bg-gray-100 text-gray-600"],
  scheduled: ["Programmato", "bg-blue-50 text-blue-700"],
  published: ["Pubblicato", "bg-green-50 text-green-700"],
  withdrawn: ["Ritirato", "bg-amber-50 text-amber-700"],
};
const campaignLabel = (i) => {
  if (i.campaign?.status === "executed") return i.notification?.status === "done" ? "Email inviata" : "Email in corso";
  if (i.campaign) return `Email prenotata · ${i.campaign.month}`;
  return i.email_requested && i.status === "draft" ? "Email richiesta" : "Solo dashboard";
};

const IconBtn = ({ onClick, title, testid, children }) => (
  <button onClick={onClick} title={title} data-testid={testid} className="p-2 rounded-full text-gray-500 hover:text-[#B40101] hover:bg-[#FAFAFA] transition-colors">{children}</button>
);

export default function AdminMarketing() {
  const [items, setItems] = useState(null);
  const [plan, setPlan] = useState(null);
  const [month, setMonth] = useState(() => romeMonth(new Date()));
  const [dialog, setDialog] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/marketing/contents").then((r) => setItems(r.data.items));
    api.get("/admin/marketing/plan", { params: { month } }).then((r) => setPlan(r.data));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const done = () => { setDialog(null); load(); };
  const act = (path, msg, method = "post") => api[method](path).then(() => { toast.success(msg); done(); })
    .catch((e) => toast.error(e?.response?.data?.detail || "Operazione non riuscita"));

  return (
    <div className="space-y-6 fade-slide" data-testid="admin-marketing-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0A0A]">Marketing PA</h1>
          <p className="text-gray-500 text-sm">Piano mensile: 1 kit, 4 consigli (uno a settimana), 2 contenuti promozionali.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" className="vg-input text-sm" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="plan-month" />
          <button onClick={() => setDialog({ type: "edit" })} className="vg-btn-primary flex items-center gap-1 px-4 py-2 text-sm whitespace-nowrap" data-testid="marketing-new-btn"><Plus size={15} /> Nuovo</button>
        </div>
      </div>
      <PlanSummary plan={plan} />
      <div className="vg-card overflow-x-auto" data-testid="marketing-admin-list">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-gray-400"><th className="p-4">Contenuto</th><th>Stato</th><th>Pubblicazione</th><th>Email</th><th className="text-right pr-4">Azioni</th></tr></thead>
          <tbody>
            {items?.length === 0 && <tr><td colSpan={5} className="p-6 text-gray-500" data-testid="marketing-admin-empty">Nessun contenuto. Crea il primo.</td></tr>}
            {items?.map((i) => {
              const [label, cls] = STATUS[i.status];
              return (
                <tr key={i.id} className="border-t border-gray-100" data-testid={`admin-content-${i.id}`}>
                  <td className="p-4"><p className="text-[10px] font-bold uppercase text-[#B40101]">{KIND_LABELS[i.kind]}</p><p className="font-semibold text-[#0A0A0A]">{i.title}</p></td>
                  <td><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`} data-testid={`admin-content-status-${i.id}`}>{label}</span></td>
                  <td className="text-xs text-gray-500">{fmtDate(i.publish_at)}</td>
                  <td className="text-xs text-gray-500" data-testid={`admin-content-notif-${i.id}`}>{campaignLabel(i)}</td>
                  <td className="text-right pr-2 whitespace-nowrap">
                    <IconBtn onClick={() => setDialog({ type: "preview", item: i })} title="Anteprima" testid={`admin-preview-${i.id}`}><Eye size={16} /></IconBtn>
                    <IconBtn onClick={() => setDialog({ type: "edit", item: i })} title="Modifica" testid={`admin-edit-${i.id}`}><Pencil size={16} /></IconBtn>
                    {i.status !== "published" && <IconBtn onClick={() => setDialog({ type: "publish", item: i })} title="Pubblica / programma" testid={`admin-publish-${i.id}`}><Send size={16} /></IconBtn>}
                    {i.status !== "draft" && i.status !== "withdrawn" && <IconBtn onClick={() => setDialog({ type: "unpublish", item: i })} title={i.status === "published" ? "Ritira" : "Annulla programmazione"} testid={`admin-unpublish-${i.id}`}><Undo2 size={16} /></IconBtn>}
                    <IconBtn onClick={() => setDialog({ type: "log", item: i })} title="Notifiche" testid={`admin-log-${i.id}`}><Mail size={16} /></IconBtn>
                    {i.status !== "published" && <IconBtn onClick={() => setDialog({ type: "delete", item: i })} title="Elimina" testid={`admin-delete-${i.id}`}><Trash2 size={16} /></IconBtn>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {dialog?.type === "edit" && <ContentEditor initial={dialog.item} onClose={() => setDialog(null)} onSaved={done} />}
      {dialog?.type === "publish" && <PublishDialog item={dialog.item} onClose={() => setDialog(null)} onDone={done} />}
      {dialog?.type === "log" && <EmailLogDialog item={dialog.item} onClose={() => setDialog(null)} />}
      {dialog?.type === "preview" && <PreviewDialog item={dialog.item} onClose={() => setDialog(null)} />}
      <ConfirmModal
        open={dialog?.type === "unpublish"} testid="confirm-unpublish"
        title={dialog?.item?.status === "scheduled" ? "Annullare la programmazione?" : "Ritirare il contenuto?"}
        message={dialog?.item?.status === "scheduled" ? "Il contenuto torna in bozza; l'eventuale campagna email prenotata viene annullata e lo slot liberato." : "I PA non lo vedranno più. Le notifiche già registrate non vengono ripetute."}
        onCancel={() => setDialog(null)} onConfirm={() => act(`/admin/marketing/contents/${dialog.item.id}/unpublish`, "Contenuto ritirato")}
      />
      <ConfirmModal
        open={dialog?.type === "delete"} title="Eliminare il contenuto?" destructive testid="confirm-delete"
        message="Il contenuto verrà rimosso dall'archivio admin."
        onCancel={() => setDialog(null)} onConfirm={() => act(`/admin/marketing/contents/${dialog.item.id}`, "Contenuto eliminato", "delete")}
      />
    </div>
  );
}
